import { inject, Service } from '@angular/core';
import { toNumber } from 'lodash';
import { Observable, throwError as observableThrowError } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { Api, ApiClient, ApiTypes } from '@app/api';
import { PayloadForFoodbacks } from '@core/interfaces/payload-for-foodbacks.interface';
import {
  toAccountFoodbacksRequest,
  toVenueFoodbacksQuery,
} from '@core/providers/backend/analytics/analytics-list-request';
import { NotificationService } from '@core/providers/notification/notification.service';
import { ContactedStatus } from '@features/venue/foodbacks/all-foodbacks/components/foodback-update-contact/foodback-update-contact.component';
import {
  FoodbackListFromBackend,
  FoodbackStatistic,
  IFoodbackContactStatus,
} from '@features/venue/foodbacks/all-foodbacks/foodback-statistic';
import { CONSTANTS } from '@shared/constants';
import { VenueScore } from '@shared/interfaces/analytics.interface';
import { Dictionary } from '@shared/interfaces/common.interface';

const CONTACTED_STATUSES: readonly string[] = Object.values(ContactedStatus);

function isContactedStatus(flag: string): flag is ContactedStatus {
  return CONTACTED_STATUSES.includes(flag);
}

function toContactedStatuses(flags: readonly string[]): ContactedStatus[] {
  return flags.filter(isContactedStatus);
}

@Service()
export class AnalyticsBackendService {
  private api = inject(ApiClient);
  private notificationService = inject(NotificationService);

  static mapScoreFromBackendToScore(score: ApiTypes.ScoreTrendPointResponseDto): VenueScore {
    return {
      date: score.date,
      value: toNumber(score.value),
    };
  }

  static keysToUpperCase<T>(obj: Dictionary<T>): Dictionary<T> {
    return Object.keys(obj).reduce<Dictionary<T>>((reduced, keyName) => {
      reduced[keyName.toUpperCase()] = obj[keyName];

      return reduced;
    }, {});
  }

  getFoodbacksList$(
    uuid: string,
    contextType: string,
    restrictions: PayloadForFoodbacks
  ): Observable<FoodbackListFromBackend> {
    const foodbacks$ =
      contextType === CONSTANTS.CONTEXT.ACCOUNT
        ? this.api.post(Api.surveys.analytics.accounts.foodbacks.paged, {
            pathParams: { accountUuid: uuid },
            ...toAccountFoodbacksRequest(restrictions),
          })
        : this.api.get(Api.surveys.analytics.venues.foodbacks.paged, {
            pathParams: { venueUuid: uuid },
            query: toVenueFoodbacksQuery(restrictions),
          });

    return foodbacks$.pipe(
      map((response): FoodbackListFromBackend => ({
        totalCount: response.meta.total,
        items: response.items.map(item =>
          this.withAgeGroupDigitsOnly({ ...item, flags: toContactedStatuses(item.flags) })
        ),
      }))
    );
  }

  updateContactStatus(foodbackUuid: string, payload: IFoodbackContactStatus): Observable<unknown> {
    return this.api
      .put(Api.surveys.analytics.foodbackCustomerInfo, {
        pathParams: { uuid: foodbackUuid },
        body: payload,
      })
      .pipe(
        tap(() =>
          this.notificationService.confirmWithKey('HOME.FOODBACKS.CONTACTED.STATUS_UPDATED')
        ),
        catchError((error: unknown): Observable<never> => {
          this.notificationService.errorSnackbarWithKey('SHARED.NOTIFICATIONS.ERROR_PROCESSING');

          return observableThrowError(() => error);
        })
      );
  }

  /** Strips the `<=` / `>=` off the two open-ended groups, which the grid shows as bare ages. */
  private withAgeGroupDigitsOnly(item: FoodbackStatistic): FoodbackStatistic {
    const uselessSignCountLimit = 2;
    const ageGroupWithSignsCharLength = 4;

    return {
      ...item,
      ageGroup:
        item.ageGroup && item.ageGroup.length === ageGroupWithSignsCharLength
          ? item.ageGroup.slice(uselessSignCountLimit, item.ageGroup.length)
          : item.ageGroup,
    };
  }
}
